import React, { useState, useEffect } from "react";
import Api from "../../utils/Api";

const COLORS = {
    darkText: "#333",
    secondaryText: "#666",
    tertiaryText: "#999",
    border: "#E0E0E0",
    cardBg: "#fff",
    bar: "#8884d8",
    barHigh: "#e74c3c",
    avgLine: "#27ae60",
    increase: "#e74c3c",
    decrease: "#27ae60",
};

const MONTH_LABELS = {
    "01": "ינו׳", "02": "פבר׳", "03": "מרץ", "04": "אפר׳",
    "05": "מאי", "06": "יוני", "07": "יולי", "08": "אוג׳",
    "09": "ספט׳", "10": "אוק׳", "11": "נוב׳", "12": "דצמ׳",
};

const BAR_AREA_HEIGHT = 160;

function formatAmount(amount) {
    return Number(amount).toLocaleString("he-IL", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function getCurrentMonthStr() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export default function TransactionHistory({ description, categoryId, currentAmount, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [months, setMonths] = useState(6);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const apiCall = categoryId
            ? Api.getCategoryHistory({ categoryId, months })
            : Api.getTransactionHistory({ description, months });
        apiCall
            .then(({ response }) => {
                if (!cancelled) {
                    setData(response);
                    setLoading(false);
                }
            })
            .catch(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [description, categoryId, months]);

    useEffect(() => {
        const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, [onClose]);

    const avg = data?.average || 0;
    const diffPercent = avg > 0 ? Math.round(((currentAmount / avg) - 1) * 1000) / 10 : 0;
    const allAmounts = data?.months?.map((m) => m.amount) || [];
    const maxAmount = Math.max(...allAmounts, currentAmount || 0, 1);
    const currentMonthStr = getCurrentMonthStr();

    return (
        <div style={overlayStyle} onClick={onClose}>
            <div style={getPopupStyle(months)} onClick={(e) => e.stopPropagation()}>
                <div style={headerStyle}>
                    <div style={titleStyle}>{description}</div>
                    <button style={closeBtnStyle} onClick={onClose}>✕</button>
                </div>

                <div style={monthSelectorStyle}>
                    {[3, 6, 12].map((m) => (
                        <button
                            key={m}
                            onClick={() => setMonths(m)}
                            style={monthBtnStyle(m === months)}
                        >
                            {m} חודשים
                        </button>
                    ))}
                </div>

                <div style={chartAreaStyle}>
                    {loading && (
                        <div style={centeredMsgStyle}>
                            <span style={{ color: COLORS.tertiaryText, fontSize: "14px" }}>טוען היסטוריה...</span>
                        </div>
                    )}

                    {!loading && (!data || data.months.length === 0) && (
                        <div style={centeredMsgStyle}>
                            <span style={{ color: COLORS.tertiaryText, fontSize: "14px" }}>אין היסטוריה</span>
                        </div>
                    )}

                    {!loading && data && data.months.length > 0 && (
                        <div style={chartInnerStyle}>
                            <div style={barsContainerStyle}>
                                {avg > 0 && <div style={avgLineStyle(maxAmount, avg)} />}
                                {data.months.map((entry) => {
                                    const isCurrent = entry.month === currentMonthStr;
                                    const isAboveAvg = entry.amount > avg * 1.15;
                                    const barH = maxAmount > 0 ? Math.max((entry.amount / maxAmount) * BAR_AREA_HEIGHT, 3) : 3;
                                    const monthNum = entry.month.split("-")[1];
                                    const year = entry.month.split("-")[0].slice(2);
                                    const label = MONTH_LABELS[monthNum] || monthNum;

                                    return (
                                        <div key={entry.month} style={columnStyle(isCurrent)}>
                                            <span style={amountStyle(isCurrent, isAboveAvg, months)}>
                                                {formatAmount(entry.amount)}
                                            </span>
                                            <div style={barWrapperStyle}>
                                                <div style={barStyle(barH, isCurrent, isAboveAvg)} />
                                            </div>
                                            <span style={monthStyle(isCurrent, months)}>{label}</span>
                                            <span style={yearStyle(isCurrent, months)}>{year}׳</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {!loading && data && data.months.length > 0 && (
                    <div style={summaryStyle}>
                        <div style={summaryItemStyle}>
                            <span style={summaryLabelStyle}>ממוצע</span>
                            <span style={summaryValueStyle}>{formatAmount(avg)}₪</span>
                        </div>
                        <div style={summaryItemStyle}>
                            <span style={summaryLabelStyle}>נוכחי</span>
                            <span style={summaryValueStyle}>{formatAmount(currentAmount)}₪</span>
                        </div>
                        {diffPercent !== 0 && (
                            <div style={diffBadgeStyle(diffPercent)}>
                                {diffPercent > 0 ? "+" : ""}{diffPercent}%
                                {diffPercent > 0 ? " מעל הממוצע" : " מתחת לממוצע"}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
};

const getPopupStyle = (monthCount) => ({
    background: COLORS.cardBg,
    borderRadius: "20px",
    boxShadow: "0 2px 16px rgba(0,0,0,0.1)",
    padding: "20px 24px",
    width: monthCount > 6 ? "860px" : "480px",
    maxWidth: "94vw",
    direction: "rtl",
    transition: "width 0.3s ease",
});

const headerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "14px",
};

const titleStyle = {
    fontSize: "18px",
    fontWeight: "bold",
    color: COLORS.darkText,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
};

const closeBtnStyle = {
    background: "none",
    border: "none",
    fontSize: "16px",
    color: COLORS.tertiaryText,
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: "6px",
    flexShrink: 0,
};

const monthSelectorStyle = {
    display: "flex",
    gap: "6px",
    marginBottom: "16px",
};

const monthBtnStyle = (active) => ({
    padding: "5px 14px",
    borderRadius: "14px",
    border: active ? "none" : `1px solid ${COLORS.border}`,
    fontSize: "13px",
    cursor: "pointer",
    background: active ? COLORS.bar : COLORS.cardBg,
    color: active ? "#fff" : COLORS.secondaryText,
    fontWeight: active ? "bold" : "normal",
    transition: "all 0.2s ease",
});

const chartAreaStyle = {
    height: `${BAR_AREA_HEIGHT + 80}px`,
};

const centeredMsgStyle = {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
};

const chartInnerStyle = {
    height: "100%",
    display: "flex",
    flexDirection: "column",
};

const barsContainerStyle = {
    position: "relative",
    display: "flex",
    gap: "3px",
    alignItems: "flex-end",
    flex: 1,
};

const columnStyle = (isCurrent) => ({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: 0,
    background: isCurrent ? "rgba(136,132,216,0.1)" : "transparent",
    borderRadius: "10px",
    padding: "4px 0",
    transition: "background 0.2s ease",
});

const amountStyle = (isCurrent, isHigh, monthCount) => ({
    fontSize: monthCount > 6
        ? (isCurrent ? "14px" : "13px")
        : (isCurrent ? "15px" : "14px"),
    fontWeight: isCurrent ? "800" : "600",
    color: isHigh ? COLORS.barHigh : COLORS.darkText,
    whiteSpace: "nowrap",
    direction: "ltr",
    marginBottom: "4px",
});

const barWrapperStyle = {
    width: "100%",
    display: "flex",
    justifyContent: "center",
};

const barStyle = (height, isCurrent, isAboveAvg) => ({
    width: isCurrent ? "70%" : "55%",
    maxWidth: isCurrent ? "40px" : "34px",
    minWidth: "8px",
    height: `${height}px`,
    borderRadius: "6px 6px 2px 2px",
    background: isAboveAvg ? COLORS.barHigh : isCurrent ? "#5cd6d6" : COLORS.bar,
    opacity: isCurrent ? 1 : 0.7,
    transition: "height 0.3s ease",
});

const avgLineStyle = (maxAmount, avg) => {
    const bottomPx = maxAmount > 0 ? (avg / maxAmount) * BAR_AREA_HEIGHT : 0;
    return {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: `${bottomPx + 38}px`,
        height: 0,
        borderTop: `1.5px dashed ${COLORS.avgLine}`,
        pointerEvents: "none",
        zIndex: 1,
    };
};

const monthStyle = (isCurrent, monthCount) => ({
    fontSize: monthCount > 6 ? "13px" : "14px",
    fontWeight: isCurrent ? "bold" : "600",
    color: isCurrent ? COLORS.darkText : "#444",
    marginTop: "5px",
});

const yearStyle = (isCurrent, monthCount) => ({
    fontSize: monthCount > 6 ? "12px" : "12px",
    color: isCurrent ? COLORS.darkText : "#555",
    fontWeight: isCurrent ? "bold" : "500",
    lineHeight: 1,
});

const summaryStyle = {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginTop: "14px",
    paddingTop: "12px",
    borderTop: `1px solid ${COLORS.border}`,
    flexWrap: "wrap",
};

const summaryItemStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
};

const summaryLabelStyle = {
    fontSize: "13px",
    color: COLORS.tertiaryText,
};

const summaryValueStyle = {
    fontSize: "16px",
    fontWeight: "bold",
    color: COLORS.darkText,
};

const diffBadgeStyle = (diff) => ({
    fontSize: "13px",
    fontWeight: "bold",
    padding: "3px 10px",
    borderRadius: "10px",
    background: diff > 0 ? "#ffeef0" : "#eefff5",
    color: diff > 0 ? COLORS.increase : COLORS.decrease,
    marginRight: "auto",
});
